import { useId, forwardRef, useMemo } from "react"
import { Layout } from "./layout.js"
import { typography, colors } from "design"
import { classes, belowViewPort } from "utils"
import { useFormContext, Controller } from "react-hook-form"

export const inputVariablesLight = {
  "--resbird-input-focus-border-color": colors.primaryBlueGrey,
  "--resbird-input-border-color": colors.grey400,
  "--resbird-input-color": colors.black,
  "--resbird-input-color-error": colors.red,
  "--resbird-input-label-color": colors.lightGrey100,
  "--resbird-input-label-error": colors.red,
  "--resbird-input-error-border-color": colors.red,
  "--resbird-input-required-color": colors.red,
  "--resbird-input-disabled-background": colors.grey200,
}

const allowedKeys = [
  "Backspace",
  "Tab",
  "ArrowLeft",
  "ArrowRight",
  "Delete",
  "Home",
  "End",
  "Enter",
]

const computeInputStyle = ({ error }) => ({
  height: 45,
  width: "100%",
  fontSize: "1rem",
  borderRadius: 4,
  color: "var(--resbird-input-color)",
  outline: 0,
  border: "1px solid",
  padding: "13px 20px",
  boxSizing: "border-box",
  borderColor: error
    ? "var(--resbird-input-error-border-color)"
    : "var(--resbird-input-border-color)",
  ":focus-within": {
    border: "2px solid",
    borderColor: error
      ? "var(--resbird-input-error-border-color)"
      : "var(--resbird-input-focus-border-color)",
  },
  ":disabled": {
    backgroundColor: "var(--resbird-input-disabled-background)",
    cursor: "not-allowed",
  },
  "::-webkit-outer-spin-button,::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  ...belowViewPort(1441, { fontSize: "0.75rem" }),
})

/**
 * Input component with customizable styles and error handling.
 *
 * This component renders a styled input field with an optional label and error message.
 * The styles are derived from a design system and can be customized via props.
 * It uses CSS variables for theming and supports error states with appropriate styling.
 *
 * @param {Object} props - The props for the Input component.
 * @param {string} [props.error] - The error, if present.
 * @param {string} [props.label] - The label text for the input field.
 * @param {Object} [props.labelStyle] - Custom styles for the label.
 * @param {Object} [props.inputStyle] - Custom styles for the input field.
 * @param {Object} [props.errorStyle] - Custom styles for the error message.
 * @param {React.Ref} ref - The ref to be forwarded to the input element.
 *
 * @returns {JSX.Element} The rendered Input component.
 *
 * @example
 *
 * import { Input } from './Input.js';
 *
 * const MyComponent = () => (
 *   <Input
 *     label="Username"
 *     error="Username is required"
 *     labelStyle={{ fontWeight: 'bold' }}
 *     inputStyle={{ borderColor: 'blue' }}
 *     errorStyle={{ color: 'red' }}
 *   />
 * );
 *
 * export default MyComponent;
 */
export const Input = forwardRef(
  (
    {
      error: propsError,
      label,
      helpText,
      fieldName = "",
      rules = {},
      className,
      labelStyle,
      errorStyle,
      required,
      isAmount,
      step,
      inputStyle: propsInputStyle,
      disabled,
      maxLength,
      onChange: propsOnChange = (val) => val,
      type = "text",
      ...props
    },
    propsRef,
  ) => {
    const id = useId()
    const errorId = useId()

    const { formState, control } = useFormContext()

    const error = useMemo(
      () =>
        !_.isEmpty(rules) || required
          ? propsError || _.get(formState.errors, fieldName)?.message
          : null,
      [fieldName, formState, propsError, required, rules],
    )

    const inputStyle = useMemo(() => computeInputStyle({ error }), [error])

    return (
      <Controller
        control={control}
        name={fieldName}
        rules={rules}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <Layout
            className={classes("input-container", className)}
            css={{
              cursor: "pointer",
            }}
            ref={ref}
            role="input"
            aria-describedby={error ? errorId : undefined}
            vertical
          >
            {label && (
              <label
                className="input-label"
                css={[
                  {
                    marginBottom: 11,
                    color: "var(--resbird-input-label-color)",
                  },
                  typography.widgetLabel,
                  labelStyle,
                ]}
                htmlFor={id}
              >
                <span>{`${label}:`}</span>
                {(rules.required || required) && (
                  <span
                    className="required-indicator"
                    css={{ color: "var(--resbird-input-required-color)" }}
                  >
                    *
                  </span>
                )}
                {helpText && (
                  <span
                    className="help-text"
                    css={{ marginLeft: 6, fontSize: "0.75rem" }}
                  >
                    {`( ${helpText} )`}
                  </span>
                )}
              </label>
            )}
            <input
              className="input"
              aria-describedby={error ? errorId : undefined}
              aria-labelledby={id}
              css={[
                inputStyle,
                propsInputStyle,
                props.rightAlign && { textAlign: "right" },
              ]}
              autoComplete="new-password"
              onBlur={(event) => {
                let { value } = event.target
                if (type === "number" && isAmount) {
                  // Trim leading zeros but retain "0." for decimal numbers
                  value = _.trimStart(value, "0")
                  // Ensure at least "0" is retained if empty after trimming
                  if (value === "." || value === "") {
                    value = "0"
                  }
                  // Ensure a valid decimal format
                  if (!value.includes(".")) {
                    value = `${value}.00` // Auto-add ".00" if missing decimals
                  }
                  if (value.match(/^\d+\.$/)) {
                    value = `${value}00` // Convert "123." -> "123.00"
                  }
                  onChange(propsOnChange(value)) // Update the form value
                }

                onBlur(event) // Call React Hook Form's onBlur
              }}
              min={0}
              maxLength={maxLength}
              ref={propsRef || ref}
              step={step ?? 1}
              disabled={disabled}
              onChange={({ target: { value } }) => {
                if (disabled) return

                if (type === "number") {
                  let newValue = value.replace(/[^0-9.]/g, "") // Allow only digits and '.'

                  // If decimals are not allowed, remove any '.'
                  if (!isAmount) {
                    newValue = newValue.replace(/\./g, "")
                  }

                  // Ensure value does not start with `.`
                  if (newValue.startsWith(".")) {
                    newValue = `0${newValue}`
                  }

                  // Allow only one decimal point
                  const parts = newValue.split(".")
                  if (parts.length > 2) {
                    newValue = `${parts[0]}.${parts.slice(1).join("")}`
                  }

                  onChange(propsOnChange(newValue))
                } else {
                  onChange(propsOnChange(value))
                }
              }}
              onWheel={(e) => e.target.blur()}
              type={type}
              inputMode={type === "number" ? "numeric" : type}
              onKeyDown={(event) => {
                if (type === "number") {
                  const { key, target } = event

                  // Allow basic navigation keys
                  if (
                    allowedKeys.includes(key) ||
                    event.ctrlKey ||
                    event.metaKey
                  ) {
                    return
                  }

                  // If isAmount is false, block decimals
                  if (!isAmount && key === ".") {
                    event.preventDefault()
                    return
                  }

                  // Prevent multiple decimal points
                  if (isAmount && key === "." && target.value.includes(".")) {
                    event.preventDefault()
                    return
                  }

                  // Prevent non-numeric characters
                  if (!/^\d$/.test(key) && key !== ".") {
                    event.preventDefault()
                  }
                }
              }}
              {...{ id, value, ...props }}
            />
            {error && (
              <label
                className="error-description"
                css={[
                  errorStyle,
                  {
                    marginTop: 5,
                    fontSize: "0.87rem",
                    color: "var(--resbird-input-label-error)",
                  },
                  belowViewPort(1441, {
                    fontSize: "0.65rem",
                    color: "red !important",
                  }),
                ]}
                htmlFor={id}
                id={errorId}
                role="aria-errormessage"
              >
                {error}
              </label>
            )}
          </Layout>
        )}
      />
    )
  },
)

Input.displayName = "Input"
